import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Observable, Subscription } from 'rxjs';
import { Queue } from '../../classes/queue/queue';
import { Request } from '../../interfaces/request';
import { ConnectionService } from '../connection/connection.service';
import { LogService } from '../log/log.service';
import { QueueService } from '../queue/queue.service';
import { Node } from '../../classes/queue/q-node';
import { ToastService } from '../toast/toast.service';
import { COLORS } from 'src/constants/colors';
import { TranslateService } from '@ngx-translate/core';
import { REQUEST_FILES_FORM_KEY, REQUEST_MAX_ATTEMPTS, REQUEST_RETRY_DELAY } from 'src/constants/request-queue';
import { base64ToBlob } from '../../utils';

@Injectable({
  providedIn: 'root'
})
export class RequestService {
  private deviceIsNative: boolean;
  private logTag = 'requestService';

  private requestQueueId = 'requestQueue';
  private requestQueue: Queue;
  private onRequestQueueChange = new EventEmitter<void>();

  // Request toast settings
  private toastColor = COLORS.primary;
  private toastMessageDuration = 1500;
  private toastMessageDelay = 1000;

  private connectionWatcher: Subscription;
  private waitToFullyConnect = 1000;
  private somethingUploaded: boolean;

  constructor(
    private connectionService: ConnectionService,
    private platform: Platform,
    private queueService: QueueService,
    private http: HttpClient,
    private logService: LogService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    this.deviceIsNative = this.platform.is('cordova');
    this.init();
  }

  public async addRequest(request: Request): Promise<void> {
    // Insert in queue
    this.requestQueue.insert(new Node(request));
    // Save queue to local when on native device
    await this.persistQueue();
    this.triggerRequestQueue();
  }

  public triggerRequestQueue() {
    this.onRequestQueueChange.emit();
  }

  private init(): void {
    this.queueService.getQueue(this.requestQueueId).then((queue) => {
      this.requestQueue = queue;
      this.watchRequestQueue();
      this.triggerRequestQueue();
    });
  }

  private watchRequestQueue(): void {
    this.onRequestQueueChange.subscribe(async () => {
      // Check if device is online
      if (await this.connectionService.deviceIsConnected()) {
        // Get first request from order
        const requestFromQueue = this.requestQueue.pop() as Request;
        // Check if not empty
        if (requestFromQueue) {
          // Time the request, a failure after a few milliseconds means it never left
          // the device, a failure after hundreds means it did reach the network
          const startedAt: number = Date.now();
          // Make the request
          const request = this.doRequest(requestFromQueue).subscribe(
            async () => {
              // Unsubscribe when result received
              request.unsubscribe();
              // Set uploaded flag for showing toast
              this.somethingUploaded = true;
              // Persist so a completed request is not replayed after an app restart
              await this.persistQueue();
              // Trigger this process again for next queued item.
              this.triggerRequestQueue();
              // Debugger logs
              this.logService.info(this.logTag, `Request succeeded in ${Date.now() - startedAt}ms:`);
              this.logService.object(requestFromQueue);
            },
            async (err) => {
              request.unsubscribe();
              await this.handleFailedRequest(requestFromQueue, err, Date.now() - startedAt);
            }
          );
        } else {
          // Check if there was something uploaded
          if (this.somethingUploaded) {
            this.somethingUploaded = false;
            // Show success toast
            this.toastService.showToastMessage(
              this.translateService.instant('toast.requestQueueFinished'),
              this.toastColor,
              this.toastMessageDuration,
              this.toastMessageDelay
            );
          }

          if (this.deviceIsNative) {
            // Remove queue from local sql when on native device
            try {
              await this.queueService.deleteSavedQueue(this.requestQueueId);
            } catch (error) {
              this.logService.error(this.logTag, 'Unable to remove the saved request queue');
              this.logService.object(error);
            }
          }
        }
      } else {
        // Check of connection is being watched
        if (!this.connectionWatcher) {
          // Watch connection and start queue when connected
          this.connectionWatcher = this.connectionService.watchConnection().subscribe(() => {
            setTimeout(() => {
              this.triggerRequestQueue();
            }, this.waitToFullyConnect);
          });
        }
      }
    });
  }

  /**
   * Retries a failed request until the attempt limit is reached, so a temporary
   * network failure no longer discards the queued change without any feedback.
   * @param request The request that failed
   * @param error The error the request failed with
   * @param elapsedMs How long the request took before it failed
   */
  private async handleFailedRequest(request: Request, error: any, elapsedMs: number): Promise<void> {
    // Debugger logs
    this.logService.error(this.logTag, `Request failed after ${elapsedMs}ms:`);
    this.logService.object(request);
    this.logService.error(this.logTag, 'Error:');
    this.logService.object(error);

    const attempts: number = (request.attempts ?? 0) + 1;

    // Give up so one permanently failing request cannot block everything behind it
    if (attempts >= REQUEST_MAX_ATTEMPTS) {
      this.logService.error(this.logTag, `Request discarded after ${attempts} attempts: ${request.url}`);
      this.toastService.showToastMessage(
        this.translateService.instant('toast.requestQueueFailed'),
        COLORS.danger,
        this.toastMessageDuration,
        this.toastMessageDelay
      );
      await this.persistQueue();
      // Trigger queue for next queued item.
      this.triggerRequestQueue();
      return;
    }

    // Put the request back so the user data is not lost
    request.attempts = attempts;
    this.requestQueue.insert(new Node(request));
    await this.persistQueue();

    // Wait before retrying, an immediate retry would spin the queue at full speed
    setTimeout(() => {
      this.triggerRequestQueue();
    }, REQUEST_RETRY_DELAY);
  }

  /**
   * Writes the current queue to local SQL so its state survives an app restart.
   * Persisting is best effort only, a storage failure may never keep the queued
   * requests from being sent, so nothing is thrown out of here.
   */
  private async persistQueue(): Promise<void> {
    if (!this.deviceIsNative) {
      return;
    }
    try {
      await this.queueService.saveQueue(this.requestQueue);
    } catch (error) {
      this.logService.error(this.logTag, 'Unable to persist request queue');
      this.logService.object(error);
    }
  }

  private doRequest(request: Request): Observable<any> {
    switch (request.method) {
      case 'GET':
        return this.http.get(request.url);
      case 'PUT':
        return this.http.put(request.url, this.getRequestBody(request));
      case 'POST':
        return this.http.post(request.url, this.getRequestBody(request));
      case 'DELETE':
        return this.http.delete(request.url, request.data);
    }
  }

  /**
   * Builds the body to send. Multipart uploads are stored in the queue as base64
   * because FormData cannot be persisted to local SQL, so the FormData instance is
   * recreated here, right before the request goes out.
   * @param request The request to build the body for
   */
  private getRequestBody(request: Request): any {
    // Requests without files send their plain data
    if (!request.files?.length) {
      return request.data;
    }

    const formData: FormData = new FormData();

    for (const file of request.files) {
      formData.append(REQUEST_FILES_FORM_KEY, base64ToBlob(file.base64, file.mimeType), file.filename);
    }

    // Append the accompanying text fields, for example the file type
    for (const [key, value] of Object.entries(request.fileFields ?? {})) {
      formData.append(key, value);
    }

    return formData;
  }
}
